import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Timeline } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

const STEPS: {
  title: string;
  body: string;
  where?: [string, ...string[]];
  whereNote?: string;
  figure?: ReactNode;
}[] = [
  {
    title: 'Write the bet down',
    body: 'A hypothesis is one bet on how to move a Key Result: a title, and the bet stated in full, such as “We believe that defaulting new programs to quarterly billing will lift ARR, because fewer of them churn monthly.” It lives under the Key Result it serves, on that Key Result’s Hypotheses tab. Anyone in the workspace can propose one. A title and the bet are all it takes, so an idea goes into Backlog in seconds and nobody has to score it on the spot. A hypothesis serves exactly one Key Result; if an idea serves two, write it twice.',
    where: ['Strategy', 'OKR Cycle', 'a Key Result', 'Hypotheses'],
  },
  {
    title: 'Score it, and let the ranking follow',
    body: 'Before a bet can be worked on it is scored on four inputs: reach (how many accounts, users or deals it touches, in a unit you name), impact (minimal, low, medium, high or massive), confidence (low 50%, medium 80% or high 100%) and effort in person-weeks. Revvly works out the score as reach × impact × confidence ÷ effort and ranks the open bets by it, highest first. You never type a score and there is no dragging: to move a bet up, change its inputs. You can also record an estimated improvement, the movement you expect on the Key Result in its own unit, which feeds gap coverage below.',
  },
  {
    title: 'See whether the plan can hit the number',
    body: 'Gap coverage answers the question a cycle owner actually has: could the bets under this Key Result plausibly close what is left? It adds up each open bet’s estimated improvement weighted by its confidence, and divides by the distance still to go. A gap of 170k with bets of 40k at 80% and 60k at 50% reads 36%. Validated bets are left out because their effect is already in the current value. The figure is a planning aid, not a forecast: it is shown on the Hypotheses tab, as “Covers 36%” beside each Key Result in the cycle and team views, and the weekly digest lists Key Results that are behind with their open bets covering under half the gap. It is hidden once the target is reached, and says how many open bets are missing an estimate.',
    where: ['Strategy', 'OKR Cycle'],
    whereNote:
      'The pill turns to a warning only when the Key Result is behind and the plan is thin.',
  },
  {
    title: 'Ask the agent for bets on a Key Result that is slipping',
    body: 'The Revvly Agent can list the Key Results in the active cycle that are falling behind, furthest behind first, and for any of them read its numbers, the bets already open and what concluded tests taught. Ask it for ideas and it proposes up to three hypotheses grounded in that, each stated as one falsifiable bet. Nothing is written until you approve the card, and approved bets land in Backlog under your name like any other proposal. If a bet repeats an existing title or overshoots the gap, the agent tells you rather than accepting the warning for you. It also reads the Business profile your admins save in Settings (what you do, for whom, your focus this period, and your constraints, which it takes into account and flags when a request goes against one), and it remembers the bets you decline: say why when you decline, and it will not offer that bet again and will take the reason into account.',
    where: ['Settings', 'Business'],
  },
  {
    title: 'Develop it, then state the test before running it',
    body: 'A bet moves to Developing once it has an owner and a complete score. Before it can move to Testing, three things are written down: the expected outcome on the Key Result’s measure, the decision rule (what you will do for each kind of result) and the planned end date. Those lock when the test starts, along with the score inputs, so the verdict is judged against what you said before you saw the numbers. A test longer than 60 days, past the cycle end, or a fourth test running on the same Key Result asks you to confirm first.',
  },
  {
    title: 'Keep tests honest while they run',
    body: 'The In flight board shows every bet across Key Results by status, with Testing ordered by planned end date and overdue ones highlighted. The owner gets a direct message two days before a test ends, with the expected outcome and buttons to conclude or extend, and one more message the day after if it is still running. After that it appears in the weekly digest until it has a verdict, rather than nagging every day. Extending needs a reason, and a third extension asks whether the test is telling you something. Link the work behind a bet (a ClickUp task, a doc, a dashboard) on its page. A linked ClickUp task shows its current title and status, read from ClickUp every half hour, and when every linked task is done the owner is asked once whether the test is live. Revvly never writes to ClickUp and never moves a bet on its own.',
    where: ['Strategy', 'Hypotheses', 'In flight'],
  },
  {
    title: 'Record the verdict, in the app or in Slack',
    body: 'Concluding records the outcome (validated, invalidated or inconclusive, with the reason when it is inconclusive), the result, what was learned and the next step. The owner decides the outcome; Revvly never infers one from the numbers, though a verdict that contradicts the observed effect asks you to confirm. You can also conclude without leaving Slack: press Conclude here on the reminder, or type /revvly conclude, and the bot shows what you expected and your decision rule, then asks for the outcome with three buttons, the result, the learning and the reason if needed. It saves through exactly the same rules as the page.',
    where: ['Strategy', 'Hypotheses', 'a hypothesis'],
  },
  {
    title: 'See the before and after on metric-backed Key Results',
    body: 'When the Key Result reads a Revvly metric, the metric’s value is captured when the test starts and again when it concludes, and shown on the hypothesis page with how far it moved. It is context for the verdict, never an input to it: other things moved the metric too. If the Key Result stopped reading its metric during the test, or the metric could not be read, the page says so instead of leaving a gap. On the Analytics page, the months in which tests on a chart’s target started or ended are marked on the MRR, ARR, NRR and expansion charts, and the target badge lists those tests.',
  },
  {
    title: 'Make the learning outlive the cycle',
    body: 'Concluded bets and their learnings collect in the Learnings library, searchable by anyone in the workspace, with filters by outcome, team and cycle. As you type a new hypothesis, up to three similar past ones appear with their outcome and learning, matched on meaning rather than only shared words, so a test that already failed is not run again by accident. An inconclusive test can be run again from its page, as a new hypothesis linked to the first. Calibration shows, for each confidence level, how often bets made at that confidence actually validated, once five or more have concluded at that level. An administrator can also post each concluded test’s outcome and learning to the announcement channel, so what one team found out reaches everyone else.',
    where: ['Settings', 'Notifications'],
    whereNote: 'Post each concluded test’s learning to the announcement channel. Off by default.',
  },
  {
    title: 'Close the cycle without stranding a test',
    body: 'A cycle cannot close while bets are still open. The cycle owner gets one panel listing every open hypothesis with a choice for each: conclude it, drop it with a reason, or carry it to a Key Result in a later cycle. A carried test keeps its pre-registration and history and reads as one test across both cycles. Nothing applies until every row is valid, and then everything applies together.',
    figure: (
      <Figure
        title="Life of a hypothesis"
        caption="A bet only reaches Testing once it is owned, scored and pre-registered, and leaves through a verdict, a drop or a carry."
      >
        <Timeline
          items={[
            {
              when: 'Backlog',
              title: 'Proposed',
              text: 'A title and the bet. Anyone can add one.',
              tone: 'muted',
            },
            {
              when: 'Developing',
              title: 'Owned and scored',
              text: 'Reach, impact, confidence and effort set.',
            },
            {
              when: 'Testing',
              title: 'Pre-registered',
              text: 'Expected outcome, decision rule and end date lock.',
              tone: 'accent',
            },
            {
              when: 'Concluded',
              title: 'Verdict and learning',
              text: 'Validated, invalidated or inconclusive.',
              tone: 'warn',
            },
          ]}
        />
      </Figure>
    ),
  },
  {
    title: 'What hypotheses are not',
    body: 'There are no targets on activity: the number of bets or tests run is shown as a health signal, never as a goal, because a count is the easiest thing to game. Revvly records the test and its verdict; it does not split traffic, manage feature flags or decide a result from statistical significance. A hypothesis never changes a Key Result’s value, confidence or score, and linked work being finished never changes a hypothesis’s status. Proposing is open to everyone; moving a bet forward belongs to the people accountable for the Key Result.',
  },
];

export function HypothesesGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Test your bets with hypotheses — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Test your bets with hypotheses</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Test your bets with <em>hypotheses</em>.
        </h1>
        <p className="dp-oneliner">
          A Key Result with a number and no plan either stalls or fills up with work nobody can
          explain. Hypotheses put the plan under the number: write each bet down, rank it on a
          shared score, state the test before you run it, record the verdict and keep what you
          learned. Works with <Link to="/docs/guides/okrs">your OKRs</Link> and the same Slack
          connection.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={step.title} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.where && <UiPath steps={step.where} note={step.whereNote} />}
          {step.figure}
        </div>
      ))}
    </div>
  );
}
